import type { ReplacementOrderResult } from "@/services/replacement-order/replacement-order.types";

export const SIM_LATENCY_MS = 300;
/** A realistic HOP submit ceiling so the pending-disabled action is honestly exercised. */
export const SLOW_LATENCY_MS = 6500;

export const SCENARIO_IDS = {
  success: "success",
  slow: "slow",
  failure: "failure",
} as const;

export type ScenarioId = (typeof SCENARIO_IDS)[keyof typeof SCENARIO_IDS];

export const SCENARIO_LABELS: Record<ScenarioId, string> = {
  [SCENARIO_IDS.success]: "Submit succeeds",
  [SCENARIO_IDS.slow]: "Slow submit",
  [SCENARIO_IDS.failure]: "Submit fails",
};

export interface ReplacementOrderScenario {
  id: ScenarioId;
  latencyMs: number;
  shouldFail: boolean;
  result: ReplacementOrderResult;
}

const SIM_RESULT: ReplacementOrderResult = { orderNumber: "LCR-SIM-0001" };

const scenario = (
  id: ScenarioId,
  overrides: Partial<Omit<ReplacementOrderScenario, "id">> = {}
): ReplacementOrderScenario => ({
  id,
  latencyMs: SIM_LATENCY_MS,
  shouldFail: false,
  result: SIM_RESULT,
  ...overrides,
});

const SCENARIOS: Record<ScenarioId, ReplacementOrderScenario> = {
  [SCENARIO_IDS.success]: scenario(SCENARIO_IDS.success),
  [SCENARIO_IDS.slow]: scenario(SCENARIO_IDS.slow, { latencyMs: SLOW_LATENCY_MS }),
  [SCENARIO_IDS.failure]: scenario(SCENARIO_IDS.failure, { shouldFail: true }),
};

const isScenarioId = (value: string): value is ScenarioId =>
  Object.values(SCENARIO_IDS).includes(value as ScenarioId);

export const resolveScenario = (id: string): ReplacementOrderScenario =>
  isScenarioId(id) ? SCENARIOS[id] : SCENARIOS[SCENARIO_IDS.success];
